import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { ScreenType } from "../../../../types/subscription/subscriptionScreen/subscriptionScreen.type";

interface ComparePlansModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ComparePlansModal: React.FC<ComparePlansModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { theme } = useTheme();

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        theme === "dark" ? "bg-black/60" : "bg-black/40"
      }`}
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-4xl rounded-2xl shadow-xl border ${
          theme === "dark"
            ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
            : "bg-white text-gray-900 border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b ${
            theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`absolute top-0 right-4 p-2 rounded-full ${
              theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Plan Cards */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            {/* Basic Screen Card */}
            <div
              className={`p-6 rounded-lg border ${
                theme === "dark"
                  ? "border-[#2A2A2A] bg-[#1A1A1A]"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="text-center mb-6">
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  Basic Screen
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className={`text-3xl font-bold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    ₹500
                  </span>
                  <span
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    /month
                  </span>
                </div>
                <button
                  className={`w-full mt-4 px-4 py-2 rounded-lg border font-medium ${
                    theme === "dark"
                      ? "border-gray-600 text-gray-300 bg-transparent"
                      : "border-gray-300 text-gray-700 bg-white"
                  }`}
                >
                  Current Plan
                </button>
              </div>
            </div>

            {/* Premium Screen Card */}
            <div
              className={`p-6 rounded-lg border ${
                theme === "dark"
                  ? "border-[#2A2A2A] bg-[#1A1A1A]"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="text-center mb-6">
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  Premium Screen
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className={`text-3xl font-bold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    ₹1500
                  </span>
                  <span
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    /month
                  </span>
                </div>
                <button
                  className="w-full mt-4 px-4 py-2 rounded-lg font-medium text-black"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>

          {/* Features Comparison Table */}
          <div
            className={`rounded-lg border overflow-hidden ${
              theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
            }`}
          >
            <table className="w-full">
              <thead
                className={theme === "dark" ? "bg-[#2A2A2A]" : "bg-gray-100"}
              >
                <tr>
                  <th
                    className={`px-6 py-4 text-left font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Features
                  </th>
                  <th
                    className={`px-6 py-4 text-center font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {ScreenType.BASIC} screen
                  </th>
                  <th
                    className={`px-6 py-4 text-center font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {ScreenType.PREMIUM} Screen
                  </th>
                </tr>
              </thead>
              <tbody
                className={
                  theme === "dark"
                    ? "bg-[#1A1A1A] divide-y divide-[#2A2A2A]"
                    : "bg-white divide-y divide-gray-200"
                }
              >
                <tr>
                  <td
                    className={`px-6 py-4 ${
                      theme === "dark" ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      Customizable Branding
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke={theme === "dark" ? "#gray-400" : "#gray-500"}
                          strokeWidth="1.5"
                        />
                        <path
                          d="m9 12 2 2 4-4"
                          stroke={theme === "dark" ? "#gray-400" : "#gray-500"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`text-lg ${
                        theme === "dark" ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      –
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-[#B6CB24] font-medium">
                      Unlimited
                    </span>
                  </td>
                </tr>
                <tr>
                  <td
                    className={`px-6 py-4 ${
                      theme === "dark" ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      Customizable Workouts
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke={theme === "dark" ? "#gray-400" : "#gray-500"}
                          strokeWidth="1.5"
                        />
                        <path
                          d="m9 12 2 2 4-4"
                          stroke={theme === "dark" ? "#gray-400" : "#gray-500"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mx-auto"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#B6CB24"
                        strokeWidth="1.5"
                      />
                      <path
                        d="m9 12 2 2 4-4"
                        stroke="#B6CB24"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mx-auto"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#B6CB24"
                        strokeWidth="1.5"
                      />
                      <path
                        d="m9 12 2 2 4-4"
                        stroke="#B6CB24"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </td>
                </tr>
                {/* Additional rows with same pattern */}
                <tr>
                  <td
                    className={`px-6 py-4 ${
                      theme === "dark" ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      Additional Features
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`text-lg ${
                        theme === "dark" ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      –
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mx-auto"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#B6CB24"
                        strokeWidth="1.5"
                      />
                      <path
                        d="m9 12 2 2 4-4"
                        stroke="#B6CB24"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparePlansModal;
